"use client"

import { useState, useMemo } from "react"
import { RotateCcw, ArrowRightLeft } from "lucide-react"
import type { Idioma } from "@/lib/landing/idioma"
import { numeroEn } from "./formato"

const T = {
  es: {
    conversor: "Conversor",
    limpiar: "Limpiar",
    peso: "Peso",
    volumen: "Volumen",
    temperatura: "Temperatura",
    valor: "Valor",
    desde: "Desde",
    intercambiar: "Intercambiar",
    a: "A",
    resultado: "Resultado",
    equivalencias: "Equivalencias rápidas",
    mil: "= 1000 g",
    milMl: "= 1000 ml",
    hielo: "= 32°F (hielo)",
    hirviendo: "= 212°F (agua hirviendo)",
    hornoMedio: "= 356°F (horno medio)",
    hornoAlto: "= 392°F (horno alto)",
    muyAlto: "= 428°F (muy alto)",
    tips: "💡 Tips cocina",
    tipsPeso: ["• 1 huevo mediano ≈ 50g", "• 1 taza harina ≈ 120g", "• 1 taza azúcar ≈ 200g"],
    tipsVolumen: ["• 1 taza = 16 cucharadas", "• Pizca = menos de 1/8 tsp", "• 1 vaso agua ≈ 250ml"],
    tipsTemperatura: ["• Bajo: 150-170°C (300-340°F)", "• Medio: 180-190°C (350-375°F)", "• Alto: 200-220°C (400-425°F)"],
  },
  en: {
    conversor: "Converter",
    limpiar: "Reset",
    peso: "Weight",
    volumen: "Volume",
    temperatura: "Temperature",
    valor: "Value",
    desde: "From",
    intercambiar: "Swap",
    a: "To",
    resultado: "Result",
    equivalencias: "Quick equivalents",
    mil: "= 1,000 g",
    milMl: "= 1,000 ml",
    hielo: "= 32°F (freezing point)",
    hirviendo: "= 212°F (boiling water)",
    hornoMedio: "= 356°F (moderate oven)",
    hornoAlto: "= 392°F (hot oven)",
    muyAlto: "= 428°F (very hot)",
    tips: "💡 Kitchen tips",
    tipsPeso: ["• 1 medium egg ≈ 50g", "• 1 cup of flour ≈ 120g", "• 1 cup of sugar ≈ 200g"],
    tipsVolumen: ["• 1 cup = 16 tablespoons", "• A pinch = less than 1/8 tsp", "• 1 glass of water ≈ 250ml"],
    tipsTemperatura: ["• Low: 150-170°C (300-340°F)", "• Moderate: 180-190°C (350-375°F)", "• Hot: 200-220°C (400-425°F)"],
  },
}

type TabType = "peso" | "volumen" | "temperatura"

const unidadesPeso = [
  { id: "g", nombre: "Gramos (g)", nombreEn: "Grams (g)", factor: 1 },
  { id: "kg", nombre: "Kilogramos (kg)", nombreEn: "Kilograms (kg)", factor: 1000 },
  { id: "oz", nombre: "Onzas (oz)", nombreEn: "Ounces (oz)", factor: 28.3495 },
  { id: "lb", nombre: "Libras (lb)", nombreEn: "Pounds (lb)", factor: 453.592 },
]

const unidadesVolumen = [
  { id: "ml", nombre: "Mililitros (ml)", nombreEn: "Millilitres (ml)", factor: 1 },
  { id: "L", nombre: "Litros (L)", nombreEn: "Litres (L)", factor: 1000 },
  { id: "cup", nombre: "Tazas (cup)", nombreEn: "US cups (cup)", factor: 236.588 },
  { id: "tbsp", nombre: "Cucharadas (tbsp)", nombreEn: "Tablespoons (tbsp)", factor: 14.787 },
  { id: "tsp", nombre: "Cucharaditas (tsp)", nombreEn: "Teaspoons (tsp)", factor: 4.929 },
]

export default function ConversorUnidadesCalculator({ idioma = "es" }: { idioma?: Idioma }) {
  const t = T[idioma]
  const [tab, setTab] = useState<TabType>("peso")
  const [valor, setValor] = useState(100)
  const [desde, setDesde] = useState("g")
  const [hasta, setHasta] = useState("kg")
  const [tempValor, setTempValor] = useState(180)
  const [tempDesde, setTempDesde] = useState<"C" | "F">("C")

  const unidades = tab === "peso" ? unidadesPeso : unidadesVolumen

  const resultado = useMemo(() => {
    if (tab === "temperatura") {
      if (tempDesde === "C") {
        return (tempValor * 9) / 5 + 32
      } else {
        return ((tempValor - 32) * 5) / 9
      }
    }

    const desdeUnidad = unidades.find((u) => u.id === desde)
    const hastaUnidad = unidades.find((u) => u.id === hasta)
    if (!desdeUnidad || !hastaUnidad) return 0

    const valorEnBase = valor * desdeUnidad.factor
    return valorEnBase / hastaUnidad.factor
  }, [tab, valor, desde, hasta, tempValor, tempDesde, unidades])

  const swap = () => {
    if (tab === "temperatura") {
      setTempDesde(tempDesde === "C" ? "F" : "C")
    } else {
      const temp = desde
      setDesde(hasta)
      setHasta(temp)
    }
  }

  const reset = () => {
    setValor(100)
    setDesde(tab === "peso" ? "g" : "ml")
    setHasta(tab === "peso" ? "kg" : "L")
    setTempValor(180)
    setTempDesde("C")
  }

  const handleTabChange = (newTab: TabType) => {
    setTab(newTab)
    if (newTab === "peso") {
      setDesde("g")
      setHasta("kg")
    } else if (newTab === "volumen") {
      setDesde("ml")
      setHasta("L")
    }
  }

  return (
    <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm overflow-hidden">
      <div className="grid lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-zinc-200">
        {/* Inputs */}
        <div className="p-6 md:p-8 space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-zinc-900">{t.conversor}</h2>
            <button
              onClick={reset}
              className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              {t.limpiar}
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 p-1 bg-zinc-100 rounded-xl">
            {(["peso", "volumen", "temperatura"] as TabType[]).map((pestana) => (
              <button
                key={pestana}
                onClick={() => handleTabChange(pestana)}
                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                  tab === pestana
                    ? "bg-white shadow text-zinc-900"
                    : "text-zinc-500 hover:text-zinc-700"
                }`}
              >
                {pestana === "peso" ? t.peso : pestana === "volumen" ? t.volumen : t.temperatura}
              </button>
            ))}
          </div>

          {/* Conversion inputs */}
          {tab !== "temperatura" ? (
            <>
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1.5">
                  {t.valor}
                </label>
                <input
                  type="number"
                  value={valor || ""}
                  onChange={(e) => setValor(parseFloat(e.target.value) || 0)}
                  placeholder="100"
                  min="0"
                  step="any"
                  className="w-full px-4 py-2.5 rounded-xl border border-zinc-200 text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent"
                />
              </div>

              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-zinc-700 mb-1.5">
                    {t.desde}
                  </label>
                  <select
                    value={desde}
                    onChange={(e) => setDesde(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-xl border border-zinc-200 text-zinc-900 focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent"
                  >
                    {unidades.map((u) => (
                      <option key={u.id} value={u.id}>
                        {idioma === "en" ? u.nombreEn : u.nombre}
                      </option>
                    ))}
                  </select>
                </div>

                <button
                  onClick={swap}
                  className="p-2.5 rounded-xl border border-zinc-200 hover:bg-zinc-50 transition-colors"
                  title={t.intercambiar}
                >
                  <ArrowRightLeft className="w-5 h-5 text-zinc-600" />
                </button>

                <div className="flex-1">
                  <label className="block text-sm font-medium text-zinc-700 mb-1.5">
                    {t.a}
                  </label>
                  <select
                    value={hasta}
                    onChange={(e) => setHasta(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-xl border border-zinc-200 text-zinc-900 focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent"
                  >
                    {unidades.map((u) => (
                      <option key={u.id} value={u.id}>
                        {idioma === "en" ? u.nombreEn : u.nombre}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1.5">
                  {t.temperatura}
                </label>
                <input
                  type="number"
                  value={tempValor || ""}
                  onChange={(e) => setTempValor(parseFloat(e.target.value) || 0)}
                  placeholder="180"
                  step="any"
                  className="w-full px-4 py-2.5 rounded-xl border border-zinc-200 text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent"
                />
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={() => setTempDesde("C")}
                  className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all ${
                    tempDesde === "C"
                      ? "bg-lime-500 text-white"
                      : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"
                  }`}
                >
                  °C → °F
                </button>
                <button
                  onClick={swap}
                  className="p-2.5 rounded-xl border border-zinc-200 hover:bg-zinc-50 transition-colors"
                >
                  <ArrowRightLeft className="w-5 h-5 text-zinc-600" />
                </button>
                <button
                  onClick={() => setTempDesde("F")}
                  className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all ${
                    tempDesde === "F"
                      ? "bg-lime-500 text-white"
                      : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"
                  }`}
                >
                  °F → °C
                </button>
              </div>
            </>
          )}
        </div>

        {/* Results */}
        <div className="p-6 md:p-8 bg-zinc-50 space-y-6">
          <h2 className="text-lg font-semibold text-zinc-900">{t.resultado}</h2>

          {/* Main result */}
          <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl p-6 text-white">
            {tab !== "temperatura" ? (
              <>
                <p className="text-sm font-medium text-orange-100 mb-1">
                  {valor} {unidades.find((u) => u.id === desde)?.id} =
                </p>
                <p className="text-4xl font-bold">
                  {idioma === "en" ? numeroEn(resultado, 4) : resultado.toFixed(4)} {unidades.find((u) => u.id === hasta)?.id}
                </p>
              </>
            ) : (
              <>
                <p className="text-sm font-medium text-orange-100 mb-1">
                  {tempValor}°{tempDesde} =
                </p>
                <p className="text-4xl font-bold">
                  {idioma === "en" ? numeroEn(resultado, 1) : resultado.toFixed(1)}°{tempDesde === "C" ? "F" : "C"}
                </p>
              </>
            )}
          </div>

          {/* Quick reference table */}
          <div className="bg-white rounded-xl border border-zinc-200 p-4">
            <h3 className="text-sm font-semibold text-zinc-900 mb-3">
              {t.equivalencias}
            </h3>
            {tab === "peso" && (
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="text-zinc-600">1 kg</div>
                <div className="font-medium text-zinc-900">{t.mil}</div>
                <div className="text-zinc-600">1 lb</div>
                <div className="font-medium text-zinc-900">= 453.6 g</div>
                <div className="text-zinc-600">1 oz</div>
                <div className="font-medium text-zinc-900">= 28.35 g</div>
                <div className="text-zinc-600">100 g</div>
                <div className="font-medium text-zinc-900">≈ 3.5 oz</div>
              </div>
            )}
            {tab === "volumen" && (
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="text-zinc-600">1 L</div>
                <div className="font-medium text-zinc-900">{t.milMl}</div>
                <div className="text-zinc-600">1 cup</div>
                <div className="font-medium text-zinc-900">= 237 ml</div>
                <div className="text-zinc-600">1 tbsp</div>
                <div className="font-medium text-zinc-900">= 15 ml</div>
                <div className="text-zinc-600">1 tsp</div>
                <div className="font-medium text-zinc-900">= 5 ml</div>
                <div className="text-zinc-600">3 tsp</div>
                <div className="font-medium text-zinc-900">= 1 tbsp</div>
              </div>
            )}
            {tab === "temperatura" && (
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="text-zinc-600">0°C</div>
                <div className="font-medium text-zinc-900">{t.hielo}</div>
                <div className="text-zinc-600">100°C</div>
                <div className="font-medium text-zinc-900">{t.hirviendo}</div>
                <div className="text-zinc-600">180°C</div>
                <div className="font-medium text-zinc-900">{t.hornoMedio}</div>
                <div className="text-zinc-600">200°C</div>
                <div className="font-medium text-zinc-900">{t.hornoAlto}</div>
                <div className="text-zinc-600">220°C</div>
                <div className="font-medium text-zinc-900">{t.muyAlto}</div>
              </div>
            )}
          </div>

          {/* Cooking tips */}
          <div className="bg-lime-50 border border-lime-200 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-lime-800 mb-2">{t.tips}</h3>
            {tab === "peso" && (
              <ul className="text-sm text-lime-700 space-y-1">
                {t.tipsPeso.map((l) => (
                  <li key={l}>{l}</li>
                ))}
              </ul>
            )}
            {tab === "volumen" && (
              <ul className="text-sm text-lime-700 space-y-1">
                {t.tipsVolumen.map((l) => (
                  <li key={l}>{l}</li>
                ))}
              </ul>
            )}
            {tab === "temperatura" && (
              <ul className="text-sm text-lime-700 space-y-1">
                {t.tipsTemperatura.map((l) => (
                  <li key={l}>{l}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
